/**
 * Missed Exits section for the AccountManager Terminal.
 *
 * Lists positions whose scheduled exit didn't go through — either OPEN whose
 * exit_time has passed, or EXIT_PENDING that have been stuck for more than
 * the backend threshold. Each row carries a one-click "Force exit" button
 * gated by a confirm dialog; on success the table refreshes automatically.
 *
 * SuperAdmin sees every active trading user; AccountManager sees only their
 * managed pool. The backend handler enforces the scope — the frontend just
 * passes the optional accountManagerUserId prop through.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertCircle, AlertTriangle, ArrowDown, ArrowUp, ArrowUpDown,
  Filter as FilterIcon, RefreshCw, Search, XCircle,
} from 'lucide-react';
import Button from '../ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import FocusedDialog from '../ui/FocusedDialog';
import Input from '../ui/Input';
import { CHIP_PILL } from '../../utils/chipColors';
import { useToast } from '../common/ToastContainer';
import missedExitsService, {
  MissedExitReason,
  MissedExitRow,
  MissedExitsListResponse,
} from '../../services/missedExitsService';

interface Props {
  accountManagerUserId?: string;
}

type ReasonFilter = 'all' | MissedExitReason;

interface Filters {
  search: string;
  reason: ReasonFilter;
  broker: string;   // empty = all brokers
}

/** Columns the user can sort by. Order matches the visual table left → right. */
type SortableColumn = 'user' | 'basket' | 'strategy' | 'symbol' | 'entered';

/** Columns that get a per-column multi-select filter popover.
 *  `entered` is intentionally excluded — date-range filter would add too much
 *  UI; sort is the primary need for that column. */
type FilterableColumn = 'user' | 'basket' | 'strategy' | 'symbol';

type SortDirection = 'asc' | 'desc';
type SortState = { column: SortableColumn; direction: SortDirection } | null;

/** Display value for sort/filter. For users / baskets / strategies we prefer
 *  the human-readable name and fall back to the id when the name is empty —
 *  consistent with what the table renders, so the chooser values match what
 *  the operator sees on screen. */
const COLUMN_VALUE: Record<SortableColumn, (r: MissedExitRow) => string> = {
  user: (r) => r.user_name || r.user_email || r.user_id,
  basket: (r) => r.basket_name || r.basket_id,
  strategy: (r) => r.strategy_name || r.strategy_id,
  symbol: (r) => r.symbol,
  entered: (r) => r.entry_filled_at,
};

const COLUMN_LABEL: Record<SortableColumn, string> = {
  user: 'User',
  basket: 'Basket',
  strategy: 'Strategy',
  symbol: 'Symbol',
  entered: 'Entered',
};

const FILTERABLE_COLUMNS: FilterableColumn[] = ['user', 'basket', 'strategy', 'symbol'];

const REASON_LABEL: Record<MissedExitRow['reason'], string> = {
  open_past_exit_time: 'Past exit time',
  exit_pending_stuck: 'Stuck pending',
};

const REASON_CHIP: Record<MissedExitRow['reason'], string> = {
  open_past_exit_time:
    'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
  exit_pending_stuck:
    'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200',
};

/**
 * Per-column filter popover — a small floating panel with an in-popover
 * search input and a checkbox list of distinct values. Tick boxes to
 * narrow the column; the underlying Set is updated via onChange.
 *
 * Click-outside dismissal is handled by the parent (via the
 * data-filter-popover marker). The popover itself never captures
 * mousedown so the parent's document listener can fire when the
 * operator clicks elsewhere.
 */
const FilterPopover: React.FC<{
  column: FilterableColumn;
  values: string[];
  selected: Set<string>;
  onChange: (next: Set<string>) => void;
  onClose: () => void;
}> = ({ column, values, selected, onChange, onClose }) => {
  const [search, setSearch] = useState('');
  const matches = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return values;
    return values.filter((v) => v.toLowerCase().includes(needle));
  }, [values, search]);

  const toggle = (v: string) => {
    const next = new Set(selected);
    if (next.has(v)) next.delete(v);
    else next.add(v);
    onChange(next);
  };

  const selectAllMatches = () => onChange(new Set(matches));
  const clear = () => onChange(new Set());

  return (
    <div
      data-filter-popover
      className="absolute z-30 mt-1 right-0 w-64 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 shadow-xl normal-case tracking-normal"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="px-3 py-2 border-b border-gray-100 dark:border-gray-700">
        <div className="relative">
          <Search className="absolute left-2 top-2 h-3.5 w-3.5 text-gray-400 pointer-events-none" />
          <input
            type="text"
            value={search}
            autoFocus
            onChange={(e) => setSearch(e.target.value)}
            placeholder={`Search ${COLUMN_LABEL[column].toLowerCase()}…`}
            className="w-full pl-7 pr-2 py-1 text-xs rounded border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
      </div>
      <div className="max-h-64 overflow-y-auto py-1">
        {matches.length === 0 ? (
          <div className="px-3 py-6 text-center text-xs text-gray-400">
            No values match
          </div>
        ) : (
          matches.map((v) => {
            const checked = selected.has(v);
            return (
              <label
                key={v}
                className="flex items-center gap-2 px-3 py-1.5 text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggle(v)}
                  className="h-3.5 w-3.5 rounded text-blue-600 focus:ring-blue-500 border-gray-300"
                />
                <span className="truncate" title={v}>{v}</span>
              </label>
            );
          })
        )}
      </div>
      <div className="px-3 py-2 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between gap-2">
        <div className="text-[10px] text-gray-500 dark:text-gray-400">
          {selected.size > 0
            ? `${selected.size} selected`
            : `${values.length} value${values.length !== 1 ? 's' : ''}`}
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={selectAllMatches}
            disabled={matches.length === 0}
            className="text-[10px] font-medium text-blue-600 dark:text-blue-400 hover:underline disabled:opacity-40 disabled:no-underline"
          >
            Select all
          </button>
          <span className="text-gray-300 dark:text-gray-600">·</span>
          <button
            type="button"
            onClick={clear}
            disabled={selected.size === 0}
            className="text-[10px] font-medium text-gray-500 dark:text-gray-400 hover:underline disabled:opacity-40 disabled:no-underline"
          >
            Clear
          </button>
          <span className="text-gray-300 dark:text-gray-600">·</span>
          <button
            type="button"
            onClick={onClose}
            className="text-[10px] font-medium text-gray-700 dark:text-gray-200 hover:underline"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

const LOGIN_CHIP_BASE =
  'inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase';

/** Broker login state chip. A logged-out session is the usual reason an exit
 *  was missed — and a Force exit will be skipped until the user logs back in. */
const LoginChip: React.FC<{ loggedIn: boolean | null }> = ({ loggedIn }) => {
  if (loggedIn === true) {
    return <span className={`${LOGIN_CHIP_BASE} ${CHIP_PILL.HEALTHY}`}>Logged in</span>;
  }
  if (loggedIn === false) {
    return <span className={`${LOGIN_CHIP_BASE} ${CHIP_PILL.ERROR}`}>Logged out</span>;
  }
  return <span className={`${LOGIN_CHIP_BASE} ${CHIP_PILL.NEUTRAL}`}>Unknown</span>;
};

function formatTimestamp(iso: string): string {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
      day: '2-digit',
      month: 'short',
    });
  } catch {
    return iso;
  }
}

const MissedExitsSection: React.FC<Props> = ({ accountManagerUserId }) => {
  const toast = useToast();
  const [data, setData] = useState<MissedExitsListResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The row currently being exited — used to disable the per-row button and
  // gate the confirm dialog.
  const [pendingRow, setPendingRow] = useState<MissedExitRow | null>(null);
  const [isExiting, setIsExiting] = useState(false);
  // Client-side filters applied to the loaded snapshot. No extra backend
  // round-trips — the list is bounded by the per-pool fan-out cap.
  const [filters, setFilters] = useState<Filters>({
    search: '',
    reason: 'all',
    broker: '',
  });
  // Per-column multi-select filters. An empty Set means "no filter on this
  // column" (i.e. all values pass) — non-empty means "show only rows whose
  // value is in the set". Distinct value sets are derived from the rows.
  const [columnFilters, setColumnFilters] = useState<Record<FilterableColumn, Set<string>>>({
    user: new Set(),
    basket: new Set(),
    strategy: new Set(),
    symbol: new Set(),
  });
  // Sort state — null means original backend ordering (stuck-pending first).
  const [sortState, setSortState] = useState<SortState>(null);
  // Which column's filter popover is currently open. Only one at a time.
  const [openFilterColumn, setOpenFilterColumn] = useState<FilterableColumn | null>(null);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await missedExitsService.list(accountManagerUserId);
      setData(response);
    } catch (e: any) {
      setError(e?.message || 'Failed to load missed exits');
    } finally {
      setIsLoading(false);
    }
  }, [accountManagerUserId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleForceExit = useCallback(async () => {
    if (!pendingRow) return;
    setIsExiting(true);
    try {
      const result = await missedExitsService.forceExit(
        pendingRow.user_id,
        pendingRow.position_id,
      );
      if ((result.exits_deferred ?? 0) > 0) {
        // Leg is still OPEN with an SL claim in flight — NOT closing yet.
        // Don't reassure that the chase will handle it; prompt a retry.
        toast.showWarning(
          `Force-exit deferred — SL settling for ${pendingRow.symbol}; retry in a moment.`,
        );
      } else if (result.exits_placed > 0) {
        toast.showSuccess(
          `Force-exit submitted — ${result.exits_placed} broker exit order placed for ${pendingRow.symbol}.`,
        );
      } else {
        toast.showSuccess(
          `Force-exit accepted — ${pendingRow.symbol} was already in EXIT_PENDING; the broker chase will close it.`,
        );
      }
      setPendingRow(null);
      // Refresh so the row reflects new state (or disappears if cleared).
      await fetchData();
    } catch (e: any) {
      toast.showError(
        e?.message || 'Force-exit failed — see CloudWatch for details.',
      );
    } finally {
      setIsExiting(false);
    }
  }, [pendingRow, toast, fetchData]);

  // Stable derived values — both depend only on `data` so React Strict Mode
  // re-renders don't re-create them. Using a fresh fallback array would
  // re-trigger the useMemo on every render (the `rows ?? []` warning).
  const rows = useMemo(() => data?.rows ?? [], [data]);
  const stuckThresholdMinutes = data?.stuck_threshold_minutes ?? 5;

  // Distinct broker list for the broker chip filter — derived from the
  // current snapshot so the chip set adapts when new brokers appear.
  const brokerOptions = useMemo(() => {
    const set = new Set<string>();
    for (const r of rows) {
      if (r.broker) set.add(r.broker);
    }
    return Array.from(set).sort();
  }, [rows]);

  // Stage 1: rows surviving the top-bar global filters (search, reason, broker).
  const globallyFilteredRows = useMemo(() => {
    const needle = filters.search.trim().toLowerCase();
    return rows.filter((r) => {
      if (filters.reason !== 'all' && r.reason !== filters.reason) return false;
      if (filters.broker && r.broker !== filters.broker) return false;
      if (!needle) return true;
      return (
        (r.user_name || '').toLowerCase().includes(needle)
        || (r.user_email || '').toLowerCase().includes(needle)
        || (r.symbol || '').toLowerCase().includes(needle)
        || (r.basket_name || '').toLowerCase().includes(needle)
        || (r.basket_id || '').toLowerCase().includes(needle)
        || (r.strategy_name || '').toLowerCase().includes(needle)
        || (r.strategy_id || '').toLowerCase().includes(needle)
        || (r.client_id || '').toLowerCase().includes(needle)
      );
    });
  }, [rows, filters]);

  // Distinct values per filterable column. Computed from globally-filtered
  // rows so the popover choice set reflects what's actually visible after the
  // top bar. Trade-off: the choice list does NOT shrink as you tick boxes in
  // OTHER column filters — kept simple for predictability.
  const distinctValues = useMemo(() => {
    const out: Record<FilterableColumn, string[]> = {
      user: [], basket: [], strategy: [], symbol: [],
    };
    for (const col of FILTERABLE_COLUMNS) {
      const set = new Set<string>();
      const getter = COLUMN_VALUE[col];
      for (const r of globallyFilteredRows) {
        const v = getter(r);
        if (v) set.add(v);
      }
      out[col] = Array.from(set).sort((a, b) => a.localeCompare(b));
    }
    return out;
  }, [globallyFilteredRows]);

  // Stage 2: also apply the per-column multi-select filters.
  const columnFilteredRows = useMemo(() => {
    const active: { col: FilterableColumn; set: Set<string> }[] = [];
    for (const col of FILTERABLE_COLUMNS) {
      if (columnFilters[col].size > 0) {
        active.push({ col, set: columnFilters[col] });
      }
    }
    if (active.length === 0) return globallyFilteredRows;
    return globallyFilteredRows.filter((r) =>
      active.every(({ col, set }) => set.has(COLUMN_VALUE[col](r))),
    );
  }, [globallyFilteredRows, columnFilters]);

  // Stage 3: sort. When sortState is null, preserve the backend's original
  // ordering (stuck-pending first, then chronological).
  const filteredRows = useMemo(() => {
    if (!sortState) return columnFilteredRows;
    const { column, direction } = sortState;
    const getter = COLUMN_VALUE[column];
    const dir = direction === 'asc' ? 1 : -1;
    return [...columnFilteredRows].sort((a, b) => {
      const av = getter(a) || '';
      const bv = getter(b) || '';
      // For the entered column compare as ISO timestamps — ISO strings sort
      // chronologically as plain strings, so localeCompare is correct.
      return av.localeCompare(bv) * dir;
    });
  }, [columnFilteredRows, sortState]);

  const summary = useMemo(() => {
    const pastExit = rows.filter((r) => r.reason === 'open_past_exit_time').length;
    const stuck = rows.filter((r) => r.reason === 'exit_pending_stuck').length;
    // Distinct accounts whose broker session is logged out — the dominant
    // cause of a missed exit. Counted per (broker, client_id) so a user with
    // many open legs counts once.
    const loggedOutAccounts = new Set(
      rows
        .filter((r) => r.logged_in === false)
        .map((r) => `${r.broker}:${r.client_id}`),
    ).size;
    return { pastExit, stuck, loggedOutAccounts };
  }, [rows]);

  const hasActiveGlobalFilter = useMemo(() => (
    filters.search.trim() !== ''
    || filters.reason !== 'all'
    || filters.broker !== ''
  ), [filters]);

  const hasActiveColumnFilter = useMemo(() => (
    FILTERABLE_COLUMNS.some((c) => columnFilters[c].size > 0)
  ), [columnFilters]);

  const hasActiveFilter = hasActiveGlobalFilter || hasActiveColumnFilter;

  // Sort cycle: none → asc → desc → none.
  const cycleSort = useCallback((column: SortableColumn) => {
    setSortState((prev) => {
      if (!prev || prev.column !== column) return { column, direction: 'asc' };
      if (prev.direction === 'asc') return { column, direction: 'desc' };
      return null;
    });
  }, []);

  // Click-outside dismissal for the open filter popover. Listens at the
  // document level and closes if the click is outside both the trigger and
  // the popover panel — using refs would be cleaner but unnecessary here
  // because we close on ANY outside click anyway.
  useEffect(() => {
    if (!openFilterColumn) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (target?.closest('[data-filter-popover]')
          || target?.closest('[data-filter-trigger]')) return;
      setOpenFilterColumn(null);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [openFilterColumn]);

  // Helper: clear ALL filters (top-bar + per-column + sort) in one click.
  const clearAllFilters = () => {
    setFilters({ search: '', reason: 'all', broker: '' });
    setColumnFilters({
      user: new Set(), basket: new Set(), strategy: new Set(), symbol: new Set(),
    });
    setSortState(null);
  };

  // Inline subcomponent — sortable + optionally-filterable <th>. Defined here
  // so it can close over `sortState`, `cycleSort`, `columnFilters`, etc.
  // without prop drilling.
  const ColumnHeader: React.FC<{
    column: SortableColumn;
    align?: 'left' | 'right';
  }> = ({ column, align = 'left' }) => {
    const sortedHere = sortState?.column === column;
    const direction = sortedHere ? sortState!.direction : null;
    const filterable = (FILTERABLE_COLUMNS as readonly string[]).includes(column);
    const filterCol = column as FilterableColumn;
    const hasFilter = filterable && columnFilters[filterCol].size > 0;
    const popoverOpen = filterable && openFilterColumn === filterCol;
    return (
      <th
        className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300 ${align === 'right' ? 'text-right' : ''}`}
      >
        <div className={`flex items-center gap-1.5 ${align === 'right' ? 'justify-end' : ''}`}>
          <button
            type="button"
            onClick={() => cycleSort(column)}
            className="inline-flex items-center gap-1 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
            title={`Sort by ${COLUMN_LABEL[column]}`}
          >
            <span>{COLUMN_LABEL[column]}</span>
            {direction === 'asc' && <ArrowUp className="h-3 w-3" />}
            {direction === 'desc' && <ArrowDown className="h-3 w-3" />}
            {!direction && <ArrowUpDown className="h-3 w-3 opacity-30" />}
          </button>
          {filterable && (
            <div className="relative">
              <button
                type="button"
                data-filter-trigger
                onClick={() => setOpenFilterColumn(popoverOpen ? null : filterCol)}
                className={`p-0.5 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors ${
                  hasFilter ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400'
                }`}
                title={`Filter by ${COLUMN_LABEL[column]}${hasFilter ? ` (${columnFilters[filterCol].size} selected)` : ''}`}
              >
                <FilterIcon className={`h-3 w-3 ${hasFilter ? 'fill-current' : ''}`} />
              </button>
              {popoverOpen && (
                <FilterPopover
                  column={filterCol}
                  values={distinctValues[filterCol]}
                  selected={columnFilters[filterCol]}
                  onChange={(next) =>
                    setColumnFilters((prev) => ({ ...prev, [filterCol]: next }))
                  }
                  onClose={() => setOpenFilterColumn(null)}
                />
              )}
            </div>
          )}
        </div>
      </th>
    );
  };

  return (
    <div className="space-y-4">
      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
            Past exit time
          </div>
          <div className="text-xl font-bold mt-1 text-red-600 dark:text-red-400 tabular-nums">
            {summary.pastExit}
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
            Logged-out accounts
          </div>
          <div className="text-xl font-bold mt-1 text-red-600 dark:text-red-400 tabular-nums">
            {summary.loggedOutAccounts}
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
            Stuck pending &gt; {stuckThresholdMinutes}m
          </div>
          <div className="text-xl font-bold mt-1 text-yellow-600 dark:text-yellow-400 tabular-nums">
            {summary.stuck}
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
            Users scanned
          </div>
          <div className="text-xl font-bold mt-1 text-gray-900 dark:text-gray-100 tabular-nums">
            {data?.scope_user_count ?? '—'}
          </div>
        </div>
      </div>

      {/* Truncation warning */}
      {data?.truncated && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-yellow-600" />
            <span className="text-yellow-800 dark:text-yellow-200 text-sm">
              Result was truncated — pool exceeded the per-request cap. Narrow scope by
              passing an AccountManager id, or contact platform admins.
            </span>
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <XCircle className="h-5 w-5 text-red-600" />
            <span className="text-red-800 dark:text-red-200">{error}</span>
          </div>
        </div>
      )}

      {/* Table card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <CardTitle>
              Missed Exits ({filteredRows.length}
              {hasActiveFilter && filteredRows.length !== rows.length
                ? ` of ${rows.length}` : ''})
            </CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchData}
              disabled={isLoading}
              leftIcon={
                <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
              }
            >
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {/* Filter bar — search + reason + broker chips. Same shape as
              the existing TerminalFilterBar so the page stays visually
              consistent between Overview and Missed Exits. */}
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400 pointer-events-none" />
                <Input
                  value={filters.search}
                  onChange={(e) =>
                    setFilters((f) => ({ ...f, search: e.target.value }))
                  }
                  placeholder="Search user, symbol, basket, strategy…"
                  className="pl-9 w-full"
                />
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {/* Reason filter chips */}
                {(
                  [
                    ['all', 'All'],
                    ['open_past_exit_time', 'Past exit'],
                    ['exit_pending_stuck', 'Stuck pending'],
                  ] as const
                ).map(([value, label]) => {
                  const active = filters.reason === value;
                  return (
                    <button
                      key={value}
                      type="button"
                      onClick={() =>
                        setFilters((f) => ({ ...f, reason: value as ReasonFilter }))
                      }
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                        active
                          ? 'bg-blue-600 text-white border-blue-600 dark:bg-blue-500 dark:border-blue-500'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-600'
                      }`}
                    >
                      {label}
                    </button>
                  );
                })}

                {/* Broker filter chips — only render when we actually have
                    more than one broker in the snapshot. Single-broker
                    deployments don't need the chip set. */}
                {brokerOptions.length > 1 && (
                  <>
                    <div className="h-5 w-px bg-gray-200 dark:bg-gray-600 mx-1" />
                    <button
                      type="button"
                      onClick={() => setFilters((f) => ({ ...f, broker: '' }))}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                        filters.broker === ''
                          ? 'bg-blue-600 text-white border-blue-600 dark:bg-blue-500 dark:border-blue-500'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-600'
                      }`}
                    >
                      All brokers
                    </button>
                    {brokerOptions.map((b) => {
                      const active = filters.broker === b;
                      return (
                        <button
                          key={b}
                          type="button"
                          onClick={() => setFilters((f) => ({ ...f, broker: b }))}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors capitalize ${
                            active
                              ? 'bg-blue-600 text-white border-blue-600 dark:bg-blue-500 dark:border-blue-500'
                              : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-600'
                          }`}
                        >
                          {b}
                        </button>
                      );
                    })}
                  </>
                )}

                {hasActiveFilter && (
                  <button
                    type="button"
                    onClick={clearAllFilters}
                    className="px-3 py-1.5 rounded-full text-xs font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                    title="Clear all filters and sort"
                  >
                    Clear all
                  </button>
                )}
              </div>
            </div>
          </div>

          {isLoading && !data ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
              <span className="ml-2 text-gray-600 dark:text-gray-400">Loading missed exits…</span>
            </div>
          ) : rows.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
              <AlertCircle className="h-8 w-8 text-green-500 mb-2" />
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                No missed exits in scope
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Every open position is either still within its scheduled exit window
                or its EXIT_PENDING chase is healthy.
              </p>
            </div>
          ) : filteredRows.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
              <Search className="h-8 w-8 text-gray-400 mb-2" />
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                No rows match the current filter
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {rows.length} missed exit{rows.length !== 1 ? 's' : ''} in scope —
                clear the filter to see them all.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-gray-50 dark:bg-gray-700 border-y border-gray-200 dark:border-gray-700">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                      Reason
                    </th>
                    <ColumnHeader column="user" />
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                      Login
                    </th>
                    <ColumnHeader column="basket" />
                    <ColumnHeader column="strategy" />
                    <ColumnHeader column="symbol" />
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                      Qty / Action
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                      Exit time
                    </th>
                    <ColumnHeader column="entered" />
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                      Detail
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((row) => (
                    <tr
                      key={`${row.user_id}-${row.position_id}`}
                      className="bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-700"
                    >
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase ${REASON_CHIP[row.reason]}`}
                        >
                          {REASON_LABEL[row.reason]}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {row.user_name || row.user_email || row.user_id.slice(0, 8)}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {row.client_id} · {row.broker}
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <LoginChip loggedIn={row.logged_in} />
                      </td>
                      <td className="px-4 py-4">
                        <div
                          className="text-sm text-gray-900 dark:text-gray-100 max-w-[180px] truncate"
                          title={row.basket_name || row.basket_id}
                        >
                          {row.basket_name || <span className="text-gray-400">—</span>}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div
                          className="text-sm text-gray-900 dark:text-gray-100 max-w-[180px] truncate"
                          title={row.strategy_name || row.strategy_id}
                        >
                          {row.strategy_name || <span className="text-gray-400">—</span>}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100 tabular-nums">
                          {row.symbol}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {row.exchange}
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 dark:text-gray-100 tabular-nums">
                          {row.quantity}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {row.action}
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-200 tabular-nums">
                        {row.scheduled_exit_time || '—'}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-200 tabular-nums">
                        {formatTimestamp(row.entry_filled_at)}
                      </td>
                      <td className="px-4 py-4 text-xs text-gray-500 dark:text-gray-400">
                        {row.reason === 'exit_pending_stuck' && row.stuck_minutes !== null
                          ? `Stuck for ${row.stuck_minutes}m`
                          : `Status ${row.position_status}`}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setPendingRow(row)}
                          disabled={isExiting && pendingRow?.position_id === row.position_id}
                        >
                          Force exit
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Confirm dialog */}
      {pendingRow && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <FocusedDialog
            ariaLabel={`Force exit ${pendingRow.symbol}`}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6"
          >
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-6 w-6 text-yellow-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                  Force exit {pendingRow.symbol}?
                </h3>
                <div className="text-sm text-gray-600 dark:text-gray-300 mt-2 space-y-1">
                  <div>
                    <strong>User:</strong>{' '}
                    {pendingRow.user_name || pendingRow.user_email || pendingRow.user_id}
                  </div>
                  <div>
                    <strong>Account:</strong> {pendingRow.client_id} · {pendingRow.broker}
                  </div>
                  <div>
                    <strong>Quantity:</strong> {pendingRow.quantity} ({pendingRow.action})
                  </div>
                  <div>
                    <strong>Reason:</strong> {REASON_LABEL[pendingRow.reason]}
                  </div>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
                  This places a fresh aggressive LIMIT exit order at the broker. The action
                  is the same as the user clicking Exit themselves — same chase, same audit
                  trail. Marked <code>manual_exit=true</code> so no follow-on re-entry fires.
                </p>
                {pendingRow.logged_in === false && (
                  <div className="mt-3 flex items-start gap-2 rounded-md bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-2.5">
                    <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                    <span className="text-xs text-red-800 dark:text-red-200">
                      This account is <strong>not logged in</strong>. The broker will reject
                      the exit until the user logs in — log the account in first, then force exit.
                    </span>
                  </div>
                )}
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" onClick={() => setPendingRow(null)} disabled={isExiting}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleForceExit}
                disabled={isExiting}
                leftIcon={
                  isExiting ? <RefreshCw className="h-4 w-4 animate-spin" /> : undefined
                }
              >
                {isExiting ? 'Submitting…' : 'Force exit'}
              </Button>
            </div>
          </FocusedDialog>
        </div>
      )}
    </div>
  );
};

export default MissedExitsSection;
